import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

// COMPONENTS Imports
import ICONS from "../../../assets/Icons";
import ConfidentialCard from "../../../components/_utils/TransactionCommonComponents/ConfidentialCard";
import TitleSubtitlePair from "../../../components/_utils/TitleSubtitlePair";
import { setUserNotificationPrefs } from "../../../redux/features/Notifications";
import { store } from "../../../redux/store";
import { notificationCreateConfiguration } from "../../../redux/features/Notifications";
import { getRefreshTokenNew } from "../../../redux/features/refreshToken/refreshToken";
import CommonAlertCard from "../../../components/_utils/CommonAlertCard";
import TEXT from "../../../utils/globalText";

// List of account & transaction notification events to disable
const ACCOUNT_NOTIFICATION_EVENTS = [
  "account_archived",
  "account_unarchived",
  "account_blocked",
  "account_updated",
  "account_other_details_edited",
  "account_pending_approval",
  "account_created",
  "account_approved",
  "account_rejected",
];

const TRANSACTION_NOTIFICATION_EVENTS = [
  "transaction_created",
  "transaction_pending_approval",
  "transaction_partially_approved",
  "transaction_approved",
  "transaction_rejected",
];

/**
 * Retrieves the current user's notification preferences.
 *
 * Strategy:
 * 1. First checks Redux state (`notificationsSlice.notificationShowConfigData`)
 *    for already-loaded preferences (fast path, no network).
 * 2. If not in state but user has a valid session (`q2p_token` in sessionStorage),
 *    dispatches `getRefreshTokenNew()` which hits the `/refresh-token` endpoint.
 *    The response includes `employees.preference_settings` with the full
 *    notification config keyed by FCM token.
 * 3. Returns the preferences object or null if unavailable.
 *
 * The returned object has the shape:
 * ```
 * {
 *   "<fcm_token>": {
 *     global_enabled: boolean,
 *     transaction_notification_enabled: boolean,
 *     account_notification_enabled: boolean
 *   }
 * }
 * ```
 */
export const getStoredUserNotificationPrefs = async () => {
  let prefs = store.getState()?.notificationsSlice?.notificationShowConfigData;
  if (!prefs && sessionStorage.getItem("q2p_token")) {
    try {
      const response = await store.dispatch(getRefreshTokenNew()).unwrap();
      prefs = response?.employees?.preference_settings || null;
    } catch (error) {
      console.error(error);
    }
  }
  return prefs;
};

/**
 * Persists notification preferences to Redux state.
 *
 * Dispatches `setUserNotificationPrefs` which updates
 * `notificationsSlice.notificationShowConfigData`. This is the single source
 * of truth for the UI; the service worker and other tabs are notified
 * separately via `broadcastNotificationPreferenceUpdate`.
 *
 */
export const setStoreUserNotificationPrefs = async (data) => {
  await store.dispatch(setUserNotificationPrefs({ preferences: data }));
};

/**
 * Reads stored preferences and fires `notificationCreateConfiguration`
 * to restore backend configuration after session timeout / login reset.
 *
 * NOTE:
 * As for now backend doesn't presist data on device level [showConfiguration API].
 * So once backend will preserve will udpate/remove this function in future
 */
export const syncNotificationConfigFromStoredPrefs = async (token) => {
  try {
    const rawPrefs = await getStoredUserNotificationPrefs();
    if (!rawPrefs) return null;

    const prefs = token && rawPrefs?.[token] ? rawPrefs[token] : rawPrefs;

    const payload = buildNotificationPayload(
      prefs?.global_enabled ?? false,
      prefs?.account_notification_enabled ?? false,
      prefs?.transaction_notification_enabled ?? false,
    );

    return await store
      .dispatch(notificationCreateConfiguration(payload))
      .unwrap();
  } catch (error) {
    console.error(error);
  }
};

// Helper to construct payload dynamically based on sub notification category
const buildNotificationPayload = (
  isGlobalEnabled,
  isAccountEnabled,
  isTxnEnabled,
) => ({
  global_enabled: isGlobalEnabled,
  events: [
    ...ACCOUNT_NOTIFICATION_EVENTS.map((event_type) => ({
      event_type,
      enabled: isAccountEnabled,
    })),
    ...TRANSACTION_NOTIFICATION_EVENTS.map((event_type) => ({
      event_type,
      enabled: isTxnEnabled,
    })),
  ],
});

const SettingsNotificationCenter = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const services = useSelector((state) => state.refreshToken.services) || [];
  const hasNotifyService =
    services?.includes("Notify") || services?.includes("Admin");
  const channelRef = useRef(null);

  const [notificationToggle, setNotificationToggle] = useState(false);
  const [transactionNotification, setTransactionNotification] = useState(false);
  const [accountNotification, setAccountNotification] = useState(false);
  const fcmToken = localStorage.getItem("fcm_token");

  const notificationConfig = useSelector(
    (state) => state.notificationsSlice.notificationShowConfigData,
  );

  /**
   * Broadcasts a notification preference change to other open tabs for the same user.
   *
   * Uses the BroadcastChannel API ("notification-preferences" channel) to send a
   * `NOTIFICATION_PREFERENCE_UPDATED` message. Other tabs listening on this channel
   * will re-fetch preferences via `getRefreshTokenNew()` (see the `useEffect` below).
   * This keeps notification toggles in sync across tabs without requiring a page reload.
   *
   */
  const broadcastNotificationPreferenceUpdate = () => {
    channelRef.current?.postMessage({
      type: "NOTIFICATION_PREFERENCE_UPDATED",
      userId: sessionStorage.getItem("user_id"),
    });
  };

  /**
   * Immutably updates the preference entry for a specific FCM token.
   *
   * Creates a new preferences object with the given `updates` merged into the
   * token's existing config. Used by all three toggle handlers to avoid mutating
   * the Redux store directly.
   *
   * @param {Object} preferenceSettings - Full preferences object keyed by token.
   * @param {string} tokenKey - The FCM token to update (from `localStorage.fcm_token`).
   * @param {Object} updates - Partial preference fields to merge (e.g., `{global_enabled: true}`).
   * @returns {Object} New preferences object with the token's entry updated.
   */
  const updatePreferenceForToken = (preferenceSettings, tokenKey, updates) => {
    return {
      ...preferenceSettings,
      [tokenKey]: {
        ...preferenceSettings[tokenKey],
        ...updates,
      },
    };
  };

  /**
   * Toggles the master notification switch (all categories on/off together).
   *
   * When the user flips the main "Notifications On This Device" toggle:
   * 1. Updates all three local state flags (eg: global, account, transaction) to the new value.
   * 2. Fetches latest preferences from redux store.
   * 3. Merges the new global state into the current token's preference entry.
   * 4. Persists to Redux, pushes to service worker, and broadcasts to other tabs.
   *
   */
  const handleToggleNotification = async () => {
    const nextState = !notificationToggle;

    setNotificationToggle(nextState);
    setTransactionNotification(nextState);
    setAccountNotification(nextState);

    const updatedData = updatePreferenceForToken(
      await getStoredUserNotificationPrefs(),
      fcmToken,
      {
        global_enabled: nextState,
        account_notification_enabled: nextState,
        transaction_notification_enabled: nextState,
      },
    );

    // Updates global and sets all categories to nextState
    const payload = buildNotificationPayload(nextState, nextState, nextState);

    try {
      await dispatch(notificationCreateConfiguration(payload)).unwrap();
    } catch (error) {
      console.error(error);
    }

    await setStoreUserNotificationPrefs(updatedData);
    broadcastNotificationPreferenceUpdate();
  };

  /**
   * Toggles account notifications only.
   *
   * Similar flow to `handleToggleNotification` but only modifies
   * `account_notification_enabled`. The global toggle remains unchanged.
   *
   */
  const handleAccountToggleNotification = async () => {
    const nextState = !accountNotification;
    setAccountNotification(nextState);

    const updatedData = updatePreferenceForToken(
      await getStoredUserNotificationPrefs(),
      fcmToken,
      {
        account_notification_enabled: nextState,
      },
    );

    // Keeps global and transaction states intact, updates account events
    const payload = buildNotificationPayload(
      notificationToggle,
      nextState,
      transactionNotification,
    );

    try {
      await dispatch(notificationCreateConfiguration(payload)).unwrap();
    } catch (error) {
      console.error(error);
    }

    await setStoreUserNotificationPrefs(updatedData);
    broadcastNotificationPreferenceUpdate();
  };

  /**
   * Toggles transaction notifications only.
   *
   * Similar flow to `handleToggleNotification` but only modifies
   * `transaction_notification_enabled`.
   *
   */
  const handleTransactionToggleNotification = async () => {
    const nextState = !transactionNotification;
    setTransactionNotification(nextState);
    const updatedData = updatePreferenceForToken(
      await getStoredUserNotificationPrefs(),
      fcmToken,
      {
        transaction_notification_enabled: nextState,
      },
    );

    // Keeps global and account states intact, updates transaction events
    const payload = buildNotificationPayload(
      notificationToggle,
      accountNotification,
      nextState,
    );
    try {
      await dispatch(notificationCreateConfiguration(payload)).unwrap();
    } catch (error) {
      console.error(error);
    }

    await setStoreUserNotificationPrefs(updatedData);
    broadcastNotificationPreferenceUpdate();
  };

  /**
   * Syncs local toggle state with Redux preference config when it changes.
   *
   * Runs when `fcmToken` or `notificationConfig` (from Redux) changes.
   * If notification permission is granted, reads the three preference flags
   * for the current token and updates local state. If permission is denied,
   * forces all toggles off (UI reflects browser-level block).
   *
   */
  useEffect(() => {
    const fetchConfig = async () => {
      if (Notification?.permission === "granted") {
        setNotificationToggle(notificationConfig?.[fcmToken]?.global_enabled);
        setAccountNotification(
          notificationConfig?.[fcmToken]?.account_notification_enabled,
        );
        setTransactionNotification(
          notificationConfig?.[fcmToken]?.transaction_notification_enabled,
        );
      } else {
        setNotificationToggle(false);
        setAccountNotification(false);
        setTransactionNotification(false);
      }
    };

    fetchConfig();
  }, [fcmToken, notificationConfig]);

  /**
   * Sets up cross-tab synchronization via BroadcastChannel.
   *
   * On mount: creates a `BroadcastChannel("notification-preferences")` and
   * listens for `NOTIFICATION_PREFERENCE_UPDATED` messages from other tabs.
   * When received (and the message is for the current user), dispatches
   * `getRefreshTokenNew()` to re-fetch preferences from the server, which
   * triggers the above `useEffect` to update local toggle state.
   *
   * On unmount: closes the channel to prevent memory leaks.
   *
   */
  useEffect(() => {
    channelRef.current = new BroadcastChannel("notification-preferences");

    channelRef.current.onmessage = (event) => {
      if (
        event.data?.type === "NOTIFICATION_PREFERENCE_UPDATED" &&
        event.data?.userId === sessionStorage.getItem("user_id")
      ) {
        dispatch(getRefreshTokenNew());
      }
    };

    return () => {
      channelRef.current.close();
    };
  }, []);

  return (
    <div className="h-screen  overflow-auto lg:overflow-hidden">
      <div className="lg:hidden fixed w-full top-top-base bg-custom-card z-z-index-content flex items-center cursor-pointer">
        <div
          onClick={() => {
            navigate(-1);
          }}
        >
          <img
            src={ICONS.CLOSE}
            alt={TEXT?.CLOSE}
            className="w-icon-md h-icon-md m-margin-md"
          />
        </div>
        <h1 className="text-base flex-1 text-text-primary">
          {TEXT?.NOTIFICATION_CENTER}
        </h1>
      </div>
      <div className="h-navbar-breadcrumb-height w-full  flex-shrink-0 Qsmd:flex hidden"></div>
      <div className="w-full Qlg:px-padding-base px-padding-md max-w-tab-width-normal mx-auto">
        <div className="flex flex-col justify-between Qsmd:mt-margin-md mt-margin-base Qsmd:pt-padding-base pt-[var(--timeline-top)]">
          {!notificationToggle && Notification?.permission !== "granted" ? (
            <CommonAlertCard
              title={TEXT?.NOTIFICATIONS_TURNED_OFF}
              texts={TEXT?.ENABLE_NOTIFICATIONS_IN_BROWSER_SETTINGS}
              icon=""
            />
          ) : null}
          <div
            className={`flex flex-col gap-gap-md overflow-hidden self-start w-full h-full ${!notificationToggle && Notification?.permission !== "granted" ? "pt-padding-md" : "pt-padding-base"}`}
          >
            <div className="w-width-full">
              <ConfidentialCard
                title={TEXT?.NOTIFICATIONS_ON_THIS_DEVICE}
                note={TEXT?.NOTIFICATIONS_ON_THIS_DEVICE_NOTE}
                toggleState={notificationToggle}
                onToggle={handleToggleNotification}
                unavailableService={!hasNotifyService}
                isEnabled={Notification?.permission === "granted"}
              />
            </div>

            <div className="border border-text-secondary"></div>

            <TitleSubtitlePair
              title={TEXT?.MANAGE_NOTIFICATIONS_BY_TYPE}
              subtitle={TEXT?.MANAGE_NOTIFICATIONS_BY_TYPE_NOTE}
              variant="sm"
              titleColor="text-primary"
            />
            <div className="flex Qsmd:flex-row flex-col gap-gap-sm w-width-full">
              <div className="Qsmd:w-setting-noticationcard-width w-width-full">
                <ConfidentialCard
                  title={TEXT?.ACCOUNTS}
                  toggleState={accountNotification}
                  onToggle={handleAccountToggleNotification}
                  isEnabled={
                    notificationToggle && Notification?.permission === "granted"
                  }
                  note={TEXT?.ACCOUNT_NOTIFICATIONS_NOTE}
                />
              </div>
              <div className="Qsmd:w-setting-noticationcard-width w-width-full">
                <ConfidentialCard
                  title={`${TEXT?.TRANSACTION}s`}
                  toggleState={transactionNotification}
                  onToggle={handleTransactionToggleNotification}
                  isEnabled={
                    notificationToggle && Notification?.permission === "granted"
                  }
                  note={TEXT?.TRANSACTION_NOTIFICATIONS_NOTE}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsNotificationCenter;
